import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../../lib/supabase';
import { buildLearningTopicPath } from '../../lib/educationContent';
import { fetchTopicFeedbackMap } from '../../lib/learningEvents';

const matchesTopicSearch = (topic, searchTerm) => {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return true;

  return [
    topic.title,
    topic.content_category,
    topic.short_summary,
    topic.plain_english_explanation,
    topic.why_this_matters
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(term));
};

const matchesResourceSearch = (resource, searchTerm) => {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return true;

  return [
    resource.title,
    resource.resource_type,
    resource.content_category,
    resource.short_description,
    resource.content_markdown
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(term));
};

const statusLabels = {
  saved: 'Saved',
  understood: 'Understood',
  confusing: 'Question',
  ask_advisor: 'Ask Advisor',
  ask_attorney: 'Ask Attorney'
};

const statusFilters = [
  { label: 'All Statuses', value: 'All' },
  { label: 'Saved', value: 'saved' },
  { label: 'Understood', value: 'understood' },
  { label: 'Questions', value: 'confusing' },
  { label: 'Ask Advisor', value: 'ask_advisor' },
  { label: 'Ask Attorney', value: 'ask_attorney' }
];

const TopicCard = ({ topic, feedbackTypes = [], onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(topic)}
    className="group flex h-full flex-col rounded-lg border border-slate-200 bg-white p-5 text-left shadow-sm transition-colors hover:border-blue-200 hover:bg-blue-50/30"
  >
    <div className="mb-4 flex items-start justify-between gap-3">
      <div className="flex flex-wrap gap-2">
        <span className="rounded bg-blue-50 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-blue-700">
          {topic.content_category}
        </span>
        {feedbackTypes.map((type) => (
          <span
            key={type}
            className="rounded bg-slate-100 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-600"
          >
            {statusLabels[type] || type}
          </span>
        ))}
      </div>
      <SafeIcon icon={FiIcons.FiArrowRight} className="mt-1 h-4 w-4 text-slate-300 transition-colors group-hover:text-blue-500" />
    </div>
    <h3 className="text-base font-bold text-slate-900">{topic.title}</h3>
    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
      {topic.short_summary || topic.plain_english_explanation}
    </p>
    {topic.why_this_matters && (
      <p className="mt-4 border-t border-slate-100 pt-3 text-xs leading-relaxed text-slate-500">
        {topic.why_this_matters}
      </p>
    )}
  </button>
);

const ResourceCard = ({ resource, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(resource)}
    className="group flex h-full flex-col rounded-lg border border-slate-200 bg-white p-5 text-left shadow-sm transition-colors hover:border-blue-200 hover:bg-blue-50/30"
  >
    <div className="mb-4 flex items-start justify-between gap-3">
      <div className="flex flex-wrap gap-2">
        <span className="rounded bg-slate-100 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-600">
          {resource.resource_type}
        </span>
        {resource.content_category && (
          <span className="rounded bg-blue-50 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-blue-700">
            {resource.content_category}
          </span>
        )}
      </div>
      <SafeIcon icon={FiIcons.FiArrowRight} className="mt-1 h-4 w-4 text-slate-300 transition-colors group-hover:text-blue-500" />
    </div>
    <h3 className="text-base font-bold text-slate-900">{resource.title}</h3>
    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
      {resource.short_description || 'Open this learning resource.'}
    </p>
    <div className="mt-4 flex items-center gap-2 border-t border-slate-100 pt-3 text-xs text-slate-500">
      <SafeIcon icon={FiIcons.FiClock} className="h-3.5 w-3.5" />
      <span>{resource.estimated_minutes || 5} min</span>
    </div>
  </button>
);

const LearningCenterPage = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [resources, setResources] = useState([]);
  const [feedbackByTopicId, setFeedbackByTopicId] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeStatusFilter, setActiveStatusFilter] = useState('All');
  const [activeLibraryView, setActiveLibraryView] = useState('topics');

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        setLoading(true);
        setError(null);

        const [{ data: topicData, error: topicsError }, { data: resourceData, error: resourcesError }] = await Promise.all([
          supabase
            .from('education_topics')
            .select(`
              id,
              title,
              slug,
              content_category,
              short_summary,
              why_this_matters,
              plain_english_explanation,
              display_priority
            `)
            .eq('publication_status', 'published')
            .order('display_priority', { ascending: true })
            .order('title', { ascending: true }),
          supabase
            .from('education_resources')
            .select('id, title, slug, resource_type, short_description, content_category, content_markdown, estimated_minutes, thumbnail_url')
            .eq('is_active', true)
            .eq('publication_status', 'published')
            .order('title', { ascending: true })
        ]);

        if (topicsError) throw topicsError;
        if (resourcesError) throw resourcesError;
        const topicRows = topicData || [];
        setTopics(topicRows);
        setResources(resourceData || []);

        if (profile?.id && topicRows.length > 0) {
          const feedbackMap = await fetchTopicFeedbackMap({
            userId: profile.id,
            topicIds: topicRows.map((topic) => topic.id)
          });
          setFeedbackByTopicId(feedbackMap);
        } else {
          setFeedbackByTopicId({});
        }
      } catch (err) {
        console.error('Learning Center load error:', err);
        setError(err.message || 'Unable to load the Learning Center.');
      } finally {
        setLoading(false);
      }
    };

    fetchTopics();
  }, [profile?.id]);

  const categories = useMemo(() => {
    const source = activeLibraryView === 'topics' ? topics : resources;
    const values = source.map((item) => item.content_category).filter(Boolean);
    return ['All', ...Array.from(new Set(values))];
  }, [topics, resources, activeLibraryView]);

  const filteredTopics = useMemo(() => {
    return topics.filter((topic) => {
      const categoryMatches = activeCategory === 'All' || topic.content_category === activeCategory;
      const feedbackTypes = feedbackByTopicId[topic.id] || [];
      const statusMatches = activeStatusFilter === 'All' || feedbackTypes.includes(activeStatusFilter);
      return categoryMatches && statusMatches && matchesTopicSearch(topic, searchTerm);
    });
  }, [topics, activeCategory, activeStatusFilter, feedbackByTopicId, searchTerm]);

  const filteredResources = useMemo(() => {
    return resources.filter((resource) => {
      const categoryMatches = activeCategory === 'All' || resource.content_category === activeCategory;
      return categoryMatches && matchesResourceSearch(resource, searchTerm);
    });
  }, [resources, activeCategory, searchTerm]);

  const featuredTopics = useMemo(() => topics.slice(0, 3), [topics]);

  const openTopic = (topic) => {
    navigate(buildLearningTopicPath({ slug: topic.slug }));
  };

  const openResource = (resource) => {
    navigate(`/education/${resource.slug}`);
  };

  const switchLibraryView = (view) => {
    setActiveLibraryView(view);
    setActiveCategory('All');
    if (view === 'resources') setActiveStatusFilter('All');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <SafeIcon icon={FiIcons.FiLoader} className="h-10 w-10 animate-spin text-slate-300" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-8 pb-20">
      <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm md:p-10">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-blue-700">Learning Center</span>
            <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900">Estate planning, in plain English</h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">
              Browse short explanations of the concepts that come up during the intake. You can go as deep as you need without leaving the workflow behind.
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Published Topics</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{topics.length}</p>
            <p className="mt-1 text-xs text-slate-500">{resources.length} resources</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md border-l-4 border-red-400 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {featuredTopics.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-800">Start Here</h2>
            <span className="text-xs text-slate-400">Common foundational topics</span>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {featuredTopics.map((topic) => (
              <TopicCard
                key={topic.id}
                topic={topic}
                feedbackTypes={feedbackByTopicId[topic.id] || []}
                onOpen={openTopic}
              />
            ))}
          </div>
        </section>
      )}

      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-1">
              {[
                { label: 'Topics', value: 'topics' },
                { label: 'Resources', value: 'resources' }
              ].map((view) => (
                <button
                  key={view.value}
                  type="button"
                  onClick={() => switchLibraryView(view.value)}
                  className={`rounded-md px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors ${
                    activeLibraryView === view.value
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {view.label}
                </button>
              ))}
            </div>
            <div className="relative max-w-xl flex-1">
              <SafeIcon icon={FiIcons.FiSearch} className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={`Search ${activeLibraryView}...`}
                className="w-full rounded-lg border border-slate-200 py-2 pl-10 pr-3 text-sm text-slate-700 outline-none transition-colors focus:border-blue-300 focus:ring-2 focus:ring-blue-500/10"
              />
            </div>
            {activeLibraryView === 'topics' && (
              <div className="flex flex-wrap gap-2">
                {statusFilters.map((status) => (
                  <button
                    key={status.value}
                    type="button"
                    onClick={() => setActiveStatusFilter(status.value)}
                    className={`rounded-md border px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors ${
                      activeStatusFilter === status.value
                        ? 'border-blue-200 bg-blue-50 text-blue-700'
                        : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {status.label}
                  </button>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  className={`rounded-md border px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors ${
                    activeCategory === category
                      ? 'border-blue-200 bg-blue-50 text-blue-700'
                      : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-5">
          {activeLibraryView === 'topics' && filteredTopics.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {filteredTopics.map((topic) => (
                <TopicCard
                  key={topic.id}
                  topic={topic}
                  feedbackTypes={feedbackByTopicId[topic.id] || []}
                  onOpen={openTopic}
                />
              ))}
            </div>
          ) : activeLibraryView === 'resources' && filteredResources.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {filteredResources.map((resource) => (
                <ResourceCard
                  key={resource.id}
                  resource={resource}
                  onOpen={openResource}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-slate-200 p-10 text-center">
              <SafeIcon icon={FiIcons.FiSearch} className="mx-auto h-10 w-10 text-slate-300" />
              <h3 className="mt-3 text-sm font-bold text-slate-900">No {activeLibraryView} found</h3>
              <p className="mt-1 text-sm text-slate-500">Try a different search, status, or category.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default LearningCenterPage;
